import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { Resolve } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';
import { combineLatest, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';

import { filterTranslationByLanguage } from '../helpers';
import { Translation } from '../models';
import { TranslationContentService, VersionService } from '../services';

import { environment } from 'src/environments/environment';

/**
 *
 * @export
 */
@Injectable({ providedIn: 'root' })
export class TranslationsResolver implements Resolve<any> {
    private appVersion: string;

    constructor(
        @Inject(LOCALE_ID) private localeCode: string,
        private http: HttpClient,
        private versionService: VersionService,
        private translate: TranslateService,
        private translationContentService: TranslationContentService
    ) {
        this.appVersion = this.versionService.getAppVersion();
    }

    resolve(): Promise<any> {
        return new Promise((resolve, _) => {
            if (environment.useOnlyStaticTranslationFiles) {
                this.http
                    .get('/assets/i18n/' + this.localeCode + `.json?cacheBuster=${this.appVersion}`)
                    .pipe(
                        take(1),
                        catchError((error) => {
                            return of([]);
                        })
                    )
                    .toPromise()
                    .then((currentStaticTranslationContent: any) => {
                        this.setTranslations(currentStaticTranslationContent);
                        resolve(currentStaticTranslationContent);
                    });
            } else {
                combineLatest([this.translationContentService.getTenantTranslation(), this.translationContentService.getTranslation()])
                    .pipe(
                        take(1),
                        catchError((error) => {
                            return of([]);
                        })
                    )
                    .toPromise()
                    .then((data: any) => {
                        if (data) {
                            const globalTranslations: Translation[] = data[0] ?? [];
                            const tenantTranslations: Translation[] = data[1] ?? [];

                            const finalTranslations: Translation[] = [...globalTranslations, ...tenantTranslations];

                            if (finalTranslations.length > 0) {
                                const currentTranslationContent = filterTranslationByLanguage(this.localeCode, finalTranslations);
                                this.setTranslations(currentTranslationContent);
                            } else {
                                this.setTranslationsFromAssets();
                            }
                        } else {
                            this.setTranslationsFromAssets();
                        }

                        resolve(data);
                    });
            }
        });
    }

    private setTranslations(translationsContent: any) {
        this.translate.resetLang(this.localeCode);
        this.translate.setTranslation(this.localeCode, translationsContent);
        this.translate.use(this.localeCode);
    }

    private setTranslationsFromAssets() {
        this.http
            .get('/assets/i18n/' + this.localeCode + `.json?cacheBuster=${this.appVersion}`)
            .pipe(take(1))
            .subscribe((currentStaticTranslationContent: any) => {
                this.setTranslations(currentStaticTranslationContent);
            });
    }
}
